import React, {
  JSX,
  JSXElementConstructor,
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  Ref,
  useCallback,
  useMemo
} from 'react';
import ReactDatePicker from 'react-datepicker';
import { appService } from '../../../Hooks/Contexts';
import { DataTestIdProp } from '../../../../Models/DataTestIdProp';
import { ConfigModel } from '@ibe/services';
import useConfig from '../../../Hooks/useConfig';
import { CustomDateDisplay, CustomDisplayProps } from './CustomDateDisplay';
import {
  DateWithKeyboardSupport,
  OnChangeDate,
  OnChangeDateIntersection
} from './DateWithKeyboardSupport';
import { CustomsProps, DatePickerProps } from './DatePickerInModal';
import { Day } from 'date-fns';
import { autoPlacement, shift } from '@floating-ui/dom';
import fallback from '../../../../_Translations/generated/date-range-input.en.t.json';
import Keys from '../../../../_Translations/generated/date-range-input.en.t.json.keys';
import useTranslation from '../../../Hooks/useTranslation';
import useMediaQuery from '../../../Hooks/useMediaQuery';
import { ApiBaseData } from '@ibe/api';
import { Select } from '../../Form/Select/Select';

export type CustomContainerProps = {
  handleDurationChange?: (numberOfNights?: number) => void;
  usePlaceholderAsOption?: boolean;
  durationNumber?: number;
  withDuration?: boolean;
};

export type ReactDatePickerRendererProps = {
  // eslint-disable-next-line
  reactDatePickerRef?: Ref<any> | undefined;
  showInModal?: boolean;
  toggleDropdown?: () => void;
} & DatePickerProps &
  DataTestIdProp &
  CustomsProps &
  CustomDisplayProps &
  CustomContainerProps;

export const NUMBER_DAY_PER_WEEK = 7;

const getCalendarStartDay = (configuration: ConfigModel, lang: string): number => {
  const weekStartsOn = configuration.calendar?.weekStartsOn || {};
  return weekStartsOn[lang] ?? weekStartsOn[lang.substring(2)] ?? weekStartsOn?.en ?? 1;
};

const ReactDatePickerRenderer = (props: ReactDatePickerRendererProps): JSX.Element => {
  const {
    reactDatePickerRef,
    dataTestId,
    locale,
    id,
    renderCustomHeader,
    onChange,
    onCalendarClose,
    onCalendarOpen,
    customInput,
    placeholderText,
    showInModal,
    withKeyboardSupport,
    toggleDropdown,
    date,
    displayFormat,
    theme,
    className,
    onInputChange,
    minDate,
    maxDate,
    inputClassName,
    useFormValidation,
    setValueToNullWhenFormatIsIncorrect,
    focusSelectedMonth,
    inline,
    noDatePickerOverflow,
    onMonthChange,
    withHeader,
    renderDayContents,
    noInitialValueInput,
    showPopperArrow,
    popperPlacement,
    handleDurationChange,
    usePlaceholderAsOption,
    durationNumber,
    withDuration
  } = props;

  const config = useConfig();
  const { t } = useTranslation('date-range-input', fallback, Keys);
  const matches = useMediaQuery('(max-width: 600px)');
  const placeholder = t(Keys.durationPlaceholder);
  const placeholderCode = 'placeholder';

  const durationOptions = useMemo(() => {
    const options = config.packageSearchDurationOptions
      ? Object.entries(config.packageSearchDurationOptions).reduce(
          (total: ApiBaseData[], [key, list], idx, array) => {
            return [
              ...total,
              ...list.map(listItem => ({
                code: key === 'WEEKS' ? `${listItem * NUMBER_DAY_PER_WEEK}` : `${listItem}`,
                description: `${listItem} ${
                  key === 'WEEKS'
                    ? t(Keys.week, { count: listItem })
                    : t(Keys.night, { count: listItem })
                }`
              })),
              idx < array.length - 1
                ? { code: '---', description: '---', isDisabled: true }
                : { code: 'filtered', description: 'filtered' }
            ].filter(element => element.code !== 'filtered');
          },
          []
        )
      : [];
    if (usePlaceholderAsOption) {
      options.unshift({ code: placeholderCode, description: placeholder });
    }
    return options;
  }, [t]);

  const handleTypeOption = useCallback((val: ApiBaseData) => {
    const isPlaceholder = val.code === placeholderCode;
    if (handleDurationChange) {
      handleDurationChange(isPlaceholder ? undefined : parseInt(val.code));
    }
  }, []);

  const popperModifiers = useMemo(
    () =>
      noDatePickerOverflow
        ? [
            shift({
              rootBoundary: 'viewport'
            })
          ]
        : [
            autoPlacement({
              allowedPlacements: ['bottom']
            })
          ],
    [noDatePickerOverflow]
  );

  const handleChange = useCallback(
    (
      dates: OnChangeDate | Date[],
      event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement> | undefined
    ): void => {
      if (showInModal) {
        if (Array.isArray(dates)) {
          if (dates.every(element => element)) {
            if (toggleDropdown) {
              toggleDropdown();
            }
          }
        }
      }
      if (!!onChange) {
        onChange(dates as OnChangeDateIntersection, event);
      }
    },
    [onChange, showInModal, toggleDropdown]
  );

  return (
    <ReactDatePicker
      {...props}
      selectsRange={props.selectsRange as undefined}
      ref={reactDatePickerRef}
      data-testid={dataTestId}
      locale={locale}
      calendarContainer={
        withDuration
          ? ({ children }: PropsWithChildren) => {
              return (
                <div className="iso__dateRangeInput__customContainer  react-datepicker">
                  <div className="iso__dateRangeInput__customContainer__top">
                    <div
                      className={`iso__dateRangeInput__customContainer__input__container iso__dateRangeInput__customContainer__input__container${
                        matches ? '--small' : ''
                      }`}
                    >
                      <div className="iso__dateRangeInput__customContainer__duration__label">
                        {t(Keys.chooseDuration)}
                      </div>
                      <div>
                        <Select
                          selectedOption={durationOptions.find(
                            option => option.code === durationNumber?.toString()
                          )}
                          getLabel={(option): string => (option ? option.description : '')}
                          getValue={(option): string => (option ? option.code : '')}
                          onChange={(val): void => handleTypeOption(val)}
                          options={durationOptions}
                          placeholder={placeholder}
                          id="packageSearchDurationInput"
                        />
                      </div>
                    </div>
                    <div className="iso__dateRangeInput__customContainer__calendar__label">
                      {t(Keys.chooseRangeLabel)}
                    </div>
                  </div>
                  <div className="iso__dateRangeInput__customContainer__calendar">{children}</div>
                </div>
              );
            }
          : undefined
      }
      customCalendarContainer={undefined}
      renderCustomHeader={
        withHeader
          ? customHeaderProps => renderCustomHeader({ ...customHeaderProps, locale })
          : undefined
      }
      renderDayContents={renderDayContents}
      onChange={handleChange}
      onCalendarOpen={onCalendarOpen}
      onCalendarClose={onCalendarClose}
      customInput={
        (customInput ||
          (withKeyboardSupport && (
            <DateWithKeyboardSupport
              date={date}
              format={displayFormat}
              theme={theme}
              id={id}
              dataTestId={dataTestId}
              placeholder={placeholderText || ''}
              className={className}
              onInputChange={onInputChange}
              minDate={minDate}
              maxDate={maxDate}
              inputClassName={inputClassName}
              useFormValidation={useFormValidation}
              setValueToNullWhenFormatIsIncorrect={setValueToNullWhenFormatIsIncorrect}
              noInitialValueInput={noInitialValueInput}
            />
          )) ||
          ((date || placeholderText) && (
            <CustomDateDisplay
              date={date}
              format={displayFormat}
              theme={theme}
              dataTestId={dataTestId}
              placeholder={placeholderText || ''}
              className={className}
            />
          ))) as ReactElement<unknown, string | JSXElementConstructor<unknown>>
      }
      placeholderText={placeholderText}
      selected={date}
      popperPlacement={popperPlacement || 'bottom-start'}
      focusSelectedMonth={focusSelectedMonth}
      popperClassName="iso__date-picker__popper"
      popperModifiers={popperModifiers}
      minDate={minDate as Date | undefined}
      maxDate={maxDate as Date | undefined}
      calendarStartDay={
        getCalendarStartDay(config, appService.lang || config.defaultLanguage) as Day | undefined
      }
      onMonthChange={onMonthChange}
      inline={inline}
      showPopperArrow={showPopperArrow}
    />
  );
};

export default ReactDatePickerRenderer;
